import { NavLink } from 'react-router-dom';
import { Icon, IconButton, cx } from '@whinerp/ui';
import { NAV_RAIL_MAX_ITEMS } from '@whinerp/ui';
import { isMessageKey, type MessageKey } from '@whinerp/contracts/i18n';
import { useT } from '../app/i18n.js';
import { useSession } from '../app/session.js';
import { buildNavigation, type NavArea } from '../app/navigation.js';

/**
 * The navigation rail.
 *
 * Items come from the entitlement set, never from a list in the source. Seven
 * at most, because an item somebody cannot use is a small tax on every session
 * - and the areas that do not fit are rendered on Home's index rather than
 * hidden behind a caret, so nothing becomes unreachable.
 *
 * Setup is the one fixed point: it never gets pushed off, because losing the
 * way into configuration is not a trade-off an ordering should be able to make.
 */

export function NavRail({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}): JSX.Element {
  const t = useT();
  const { session } = useSession();
  const enabled = new Set(
    session.entitlements.filter((entry) => entry.state === 'on').map((entry) => entry.key),
  );
  const navigation = buildNavigation({ enabled, order: session.preferences.navOrder });

  return (
    <nav
      data-component="NavRail"
      aria-label={t('nav.primary')}
      /*
       * Its own scroll, independent of the page's.
       *
       * Sticky and exactly one viewport tall, so a long list of groups scrolls
       * inside the rail while the screen beside it scrolls on its own. Without
       * the height the rail simply grows and the two are one scroll: reading to
       * the bottom of a contact list carries the navigation off the top of the
       * window, and getting back to it means scrolling the record away.
       */
      className={cx(
        'sticky top-0 flex h-screen shrink-0 flex-col gap-1 overflow-y-auto overscroll-contain',
        'border-e border-e-rule bg-surface py-3',
        'transition-[width] duration-state ease-out',
        collapsed ? 'w-rail-collapsed px-2' : 'w-rail-expanded px-3',
      )}
    >
      {/*
        * Grouped, and a group is drawn only when something in it is entitled.
        *
        * A Core-only workspace sees three groups; one with every module sees
        * all of them. That is what the seven-item rule asks for - seven for
        * the tenant's enabled set, not seven of the product's surface with
        * the remainder hidden behind an index.
        */}
      {navigation.groups
        .filter((group) => group.key !== 'admin')
        .map((group) => (
          <div key={group.key} className="flex flex-col">
            {/*
              * The section, set in the ink rather than the label grey.
              *
              * A header the same weight and colour as the items under it is
              * not a header - it reads as another item that happens not to be
              * clickable. Medium is as heavy as this design system goes; the
              * separation comes from weight, tracking and the space above.
              */}
            {group.label === null || collapsed ? null : (
              <p className="px-2 pb-1.5 pt-4 text-micro font-medium uppercase tracking-kicker text-ink">
                {t(group.label as MessageKey)}
              </p>
            )}
            <ul className="flex flex-col gap-px">
              {group.items.map((area) => (
                <li key={area.key}>
                  <NavItem area={area} collapsed={collapsed} label={t(navLabelKey(area.key))} />
                </li>
              ))}
            </ul>
          </div>
        ))}

      {/* Setup and help, against the foot of the rail with a rule above. */}
      {navigation.groups
        .filter((group) => group.key === 'admin')
        .map((group) => (
          <div key={group.key} className="mt-auto flex flex-col border-t border-t-rule pt-2">
            {collapsed ? null : (
              <p className="px-2 pb-1.5 pt-2 text-micro font-medium uppercase tracking-kicker text-ink">
                {t(group.label as MessageKey)}
              </p>
            )}
            <ul className="flex flex-col gap-px">
              {group.items.map((area) => (
                <li key={area.key}>
                  <NavItem area={area} collapsed={collapsed} label={t(navLabelKey(area.key))} />
                </li>
              ))}
            </ul>
          </div>
        ))}

      <div className="pt-2">
        <IconButton
          icon={collapsed ? 'Sidebar' : 'SidebarSimple'}
          label={collapsed ? t('nav.expand') : t('nav.collapse')}
          onClick={onToggle}
        />
      </div>
    </nav>
  );
}

function NavItem({
  area,
  collapsed,
  label,
}: {
  area: NavArea;
  collapsed: boolean;
  label: string;
}): JSX.Element {
  /*
   * An area that is named and not built is drawn and disabled.
   *
   * Not a link: there is no route behind it, and a nav item that navigates
   * nowhere is the control wired to nothing this project keeps refusing to
   * ship. It says what it is waiting for instead.
   */
  if (area.notBuilt !== undefined) {
    return (
      <span
        data-nav-unavailable={area.key}
        aria-disabled="true"
        title={label}
        className={cx(
          'flex h-touch cursor-not-allowed items-center gap-3 rounded-control text-body text-ink-disabled',
          collapsed ? 'justify-center px-0' : 'px-3',
        )}
      >
        <Icon name={area.icon} decorative />
        {collapsed ? null : <span className="truncate">{label}</span>}
      </span>
    );
  }
  return (
    <NavLink
      to={area.route}
      end={area.route === '/'}
      // Collapsed, the rail is narrower than the minimum touch target, so the
      // row still claims the full target height.
      className={({ isActive }) =>
        cx(
          'flex h-touch items-center gap-3 rounded-control text-body transition-colors duration-state ease-out',
          collapsed ? 'justify-center px-0' : 'px-3',
          isActive
            ? 'bg-bronze-tint font-medium text-bronze-text'
            : 'text-ink-600 hover:bg-sunken hover:text-ink',
        )
      }
      // Collapsed, the icon is the only thing there, so it carries the label.
      {...(collapsed ? { 'aria-label': label, title: label } : {})}
    >
      <Icon name={area.icon} decorative />
      {collapsed ? null : <span className="truncate">{label}</span>}
    </NavLink>
  );
}

/**
 * The message key for a navigation area.
 *
 * Derived rather than mapped. This was a switch over area keys with a
 * `default` returning 'nav.setup', so the two areas nobody had written labels
 * for - leads and revenue - rendered as "Setup", alongside the real Setup. A
 * person navigating saw the same confident word three times and no way to tell
 * which was which.
 *
 * The switch was a second list of the areas, kept by hand beside the real one
 * in navigation.ts. Deriving `nav.<key>` leaves one list, and the catalogue
 * decides whether it exists.
 *
 * It throws rather than falling back. A missing label is a mistake somebody
 * made minutes ago and can fix; a plausible wrong word is one nobody notices
 * for months. The nav rail has its own error boundary, so this shows as a
 * region that failed rather than taking the page down - loud, contained, and
 * caught by `navigation.test.ts` long before that.
 */
export function navLabelKey(key: string): MessageKey {
  const candidate = `nav.${key}`;
  if (!isMessageKey(candidate)) {
    throw new Error(
      `Navigation area "${key}" has no label: add '${candidate}' to the message catalogue. ` +
        `Rendering a fallback would put a confident wrong word in the one place a person navigates from.`,
    );
  }
  return candidate;
}

export { NAV_RAIL_MAX_ITEMS };
